import type { DriverRegistrationShare } from "../../../types/driver-registration-share.type";
import type { Organization } from "../../../types/organization.type";

const SHARE_COLUMNS: { key: keyof DriverRegistrationShare; label: string }[] = [
  { key: "organizationId", label: "Organization" },
  { key: "sharedAt", label: "Shared At" },
  { key: "acceptedAt", label: "Accepted At" },
  { key: "declinedAt", label: "Declined At" },
  { key: "createdAt", label: "Created At" },
  { key: "updatedAt", label: "Updated At" },
];

function formatDate(value: unknown): string {
  if (!value) {
    return "-";
  }
  const date = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString();
}

const DATE_COLUMN_KEYS = new Set<keyof DriverRegistrationShare>([
  "sharedAt",
  "acceptedAt",
  "declinedAt",
  "createdAt",
  "updatedAt",
]);

function formatShareCell(
  share: DriverRegistrationShare,
  key: keyof DriverRegistrationShare,
  organizations: Organization[]
) {
  const value = share[key];

  if (key === "organizationId") {
    const organization = organizations.find(
      (organization) => organization.organizationId === value
    );
    return organization?.displayName || organization?.name || String(value);
  }

  if (DATE_COLUMN_KEYS.has(key)) {
    return formatDate(value);
  }

  return String(value ?? "");
}

type DriverRegistrationSharesTableComponentProps = {
  shares: DriverRegistrationShare[];
  organizations: Organization[];
};

export function DriverRegistrationSharesTableComponent({
  shares,
  organizations,
}: DriverRegistrationSharesTableComponentProps) {
  if (!shares?.length) {
    return (
      <p className="shares-table-empty">
        Not shared with any organizations yet.
      </p>
    );
  }

  return (
    <table className="shares-table">
      <thead>
        <tr>
          {SHARE_COLUMNS.map((column) => (
            <th key={column.key}>{column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {shares.map((share, index) => (
          <tr key={`${share.organizationId}-${index}`}>
            {SHARE_COLUMNS.map((column) => (
              <td key={column.key}>
                {formatShareCell(share, column.key, organizations)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
